import "../styles/Aboutus.css";
import { useState, useEffect, useRef } from "react";
import { motion, useAnimation, AnimatePresence } from "framer-motion";
import { useInView } from "react-intersection-observer";
import { useNavigate } from "react-router-dom";
import ContactPage from "./ContactPage";
// Add these imports at the top with other imports
import { createPortal } from 'react-dom';
import ProductModal from './ProductModal'; // We'll create this component
import "../styles/Products.css"; // We'll create this CSS file



const Aboutus = () => {
  const [activeButton, setActiveButton] = useState(0);
  // Add these state variables after other useState declarations
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const navigate = useNavigate();
  const [ref, inView] = useInView({ threshold: 0.1, triggerOnce: true });
  const controls = useAnimation();
  const [openSection, setOpenSection] = useState(null);
  const progressRef = useRef(null);
  const progressLineRef = useRef(null);
  const products = [
    {
      id: 1,
      title: "Mobile Applications",
      icon: "📱",
      description: "Native & cross-platform mobile solutions",
      color: "#137eb1",
      examples: [
        {
          name: "Lookit - Tamil Calendar & Knowledge",
          platform: "Android",
          playStoreId: "com.makkal.calendar",
          description: "Daily companion with Tamil Panchangam 2025, educational content, and productivity tools",
          features: [
            "Offline Tamil calendar with Panchangam",
            "Daily horoscope & auspicious timings",
            "Educational videos & articles",
            "Productivity calculators & tools",
            "Event reminders & notifications"
          ]
        }
      ],
      details: {
        features: [
          "Native iOS & Android Development",
          "Cross-platform React Native/Flutter Apps",
          "UI/UX Design & Prototyping",
          "API Integration & Backend Services",
          "App Store Optimization & Deployment",
          "Maintenance & Ongoing Support"
        ],
        technologies: ["React Native", "Flutter", "Swift", "Kotlin", "Firebase", "REST APIs"],
        benefits: [
          "Enhanced customer engagement & retention",
          "Increased brand visibility 24/7",
          "Streamlined business processes",
          "Real-time analytics & user insights",
          "Offline functionality capabilities"
        ]
      }
    },
    {
      id: 2,
      title: "Websites & Web Applications",
      icon: "🌐",
      description: "Responsive, scalable web solutions",
      color: "#4A1C40",
      examples: [
        {
          name: "Readers Menu",
          url: "https://readersmenu.com",
          type: "Content Platform",
          description: "Daily digest of curated articles & news",
          features: [
            "Modern JavaScript application",
            "Curated content delivery",
            "Responsive reading experience",
            "Clean, focused interface"
          ]
        },
        {
          name: "TodayTalks",
          url: "https://todaytalks.in",
          type: "News Portal",
          description: "Latest news and updates across categories",
          features: [
            "Multi-category news aggregation",
            "Sports, tech, entertainment coverage",
            "Regular content updates",
            "Simple navigation structure"
          ]
        },
        {
          name: "Makkal Calendar",
          url: "https://makkalcalendar.mpeoples.in",
          type: "Cultural Utility",
          description: "Tamil calendar with Panchangam & astrology",
          features: [
            "Daily Panchangam details",
            "Auspicious timing calculations",
            "Horoscope & astrological data",
            "Mobile app integration"
          ]
        },
        {
          name: "MPeoples Corporate",
          url: "https://mpeoplesdemo.com",
          type: "Business Website",
          description: "Enterprise IT & business solutions showcase",
          features: [
            "Service portfolio display",
            "Client statistics & achievements",
            "Professional corporate identity",
            "Lead generation & consultation"
          ]
        }
      ],
      details: {
        features: [
          "Custom Web Development",
          "E-commerce & CMS Solutions",
          "Progressive Web Apps (PWA)",
          "SEO Optimization & Performance",
          "Web Hosting & Maintenance",
          "Security & Compliance"
        ],
        technologies: ["React", "Next.js", "Node.js", "MongoDB", "WordPress", "Shopify"],
        benefits: [
          "24/7 global accessibility",
          "Improved brand credibility",
          "Higher conversion rates",
          "Better customer insights",
          "Scalable infrastructure"
        ]
      }
    }
  ];
  // Add this function to handle product click
  const handleProductClick = (product) => {
    setSelectedProduct(product);
    setShowProductModal(true);
  };
  const toggleSection = (index, event) => {
    if (openSection === index) {
      setOpenSection(null);
    } else {
      setOpenSection(index);
      const sectionElement = event.target.closest(".service-item");
      const sectionTop = sectionElement.offsetTop;
      const sectionHeight = sectionElement.offsetHeight;
      if (progressRef.current) {
        progressRef.current.style.top = `${sectionTop}px`;
      }
      if (progressLineRef.current) {
        progressLineRef.current.style.top = `${sectionTop}px`;
        progressLineRef.current.style.height = `${sectionHeight}px`;
      }
    }
  };
  useEffect(() => {
    if (progressRef.current) {
      const expandedSections = document.querySelectorAll(
        ".service-content.open"
      );
      const newHeight = 50 + expandedSections.length * 40;
      progressRef.current.style.height = `${newHeight}px`;
    }
  }, [openSection]);
  useEffect(() => {
    if (inView) {
      controls.start("visible");
    }
  }, [controls, inView]);
  const handleClicked = () => {
    navigate("/contact");
  };
  const handleClick = (buttonIndex) => {
    setActiveButton(activeButton === buttonIndex ? 0 : buttonIndex);
  };
  const controlsLeft = useAnimation();
  const controlsCenter = useAnimation();
  const controlsRight = useAnimation();
  const [refLeft, inViewLeft] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });
  const [refCenter, inViewCenter] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });
  const [refRight, inViewRight] = useInView({
    threshold: 0.1,
    triggerOnce: true
  });
  useEffect(() => {
    if (inViewLeft) controlsLeft.start("visible");
    if (inViewCenter) controlsCenter.start("visible");
    if (inViewRight) controlsRight.start("visible");
  }, [
    controlsLeft,
    controlsCenter,
    controlsRight,
    inViewLeft,
    inViewCenter,
    inViewRight
  ]);
  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 100, damping: 10 }
    }
  };
  const cardVariants = {
    hidden: { scale: 0.95, opacity: 0 },
    visible: (i) => ({
      scale: 1,
      opacity: 1,
      transition: {
        delay: i * 0.1,
        type: "spring",
        stiffness: 100
      }
    }),
    hover: { y: -5, boxShadow: "0 10px 20px rgba(0,0,0,0.1)" }
  };
  const services = [
    {
      img: "/images/01.webp",
      title: "Commitment",
      description:
        "We are committed to delivering exceptional service, ensuring customer satisfaction through quality, reliability and continuous improvement in every interaction."
    },
    {
      img: "/images/02.webp",
      title: "Excellence",
      description:
        "We specialize in crafting innovative solutions that drive success, delivering impactful results that benefit both businesses and their customers."
    },
    {
      img: "/images/03.webp",
      title: "Passion",
      description:
        "Through constant iteration, we refine our product to better meet user needs, ensuring continuous improvement and enhanced user satisfaction."
    },
    {
      img: "/images/04.webp",
      title: "Priority",
      description:
        "Understanding customer needs and delivering exceptional service ensures customer satisfaction, fostering loyalty, trust, and long-term business success"
    },
    {
      img: "/images/05.webp",
      title: "Team Work",
      description:
        "We create a transparent and socially responsible culture where everyone can reach their potential and feel part of the team."
    },
    {
      img: "/images/06.webp",
      title: "Our Core Values",
      description:
        "Our values form the way we work with our clients, delivering outstanding user experience supported by leading edge technologies"
    }
  ];
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        when: "beforeChildren",
        staggerChildren: 0.3
      }
    }
  };
  const itemVariantsLeft = {
    hidden: { x: -100, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 50, damping: 10 }
    }
  };
  const itemVariantsRight = {
    hidden: { x: 100, opacity: 0 },
    visible: {
      x: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 50, damping: 10 }
    }
  };
  const itemVariantsCenter = {
    hidden: { y: 50, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { type: "spring", stiffness: 50, damping: 10 }
    }
  };
  const buttonVariants = {
    hidden: { scale: 0.8, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: { delay: 0.5, type: "spring", stiffness: 200 }
    },
    hover: { scale: 1.05 },
    tap: { scale: 0.95 }
  };
  const letters = ["M", "P", "E", "O", "P", "L", "E", "S"];
  const contents = {
    0: {
      text: "“Masters” of designing.",
      image: "/images/5.webp"
    },
    1: {
      text: "“Principle” of maintaining guidelines. ",
      image: "/images/6.webp"
    },
    2: {
      text: "“Enrichment” by providing strategies.",
      image: "/images/7.webp"
    },
    3: {
      text: "“Objective” to rank at a high point.",
      image: "/images/8.webp"
    },
    4: {
      text: "“Passionate” to deliver quality results.",
      image: "/images/9.webp"
    },
    5: {
      text: "“Loyalty” to the customer.",
      image: "/images/10.webp"
    },
    6: {
      text: "“Excellence” in providing the result.",
      image: "/images/11.webp"
    },
    7: {
      text: "“Success” Through Collaboration.",
      image: "/images/5.webp"
    }
  };
  const cleanedContents = {
    0: {
      text: "MASTERS"
    },
    1: {
      text: "PRINCIPLE"
    },
    2: {
      text: "ENRICHMENT"
    },
    3: {
      text: "OBJECTIVE"
    },
    4: {
      text: "PASSION"
    },
    5: {
      text: "LOYALTY"
    },
    6: {
      text: "EXCELLENCE"
    },
    7: {
      text: "SUCCESS"
    }
  };
  const services1 = [
    {
      title: "M - “Masters”",
      details: "“Masters” of designing."
    },
    {
      title: "P - “Principle”",
      details: "“Principle” of maintaining guidelines."
    },
    {
      title: "E - “Enrichment”",
      details: "“Enrichment” by providing strategies."
    },
    {
      title: "O - “Objective”",
      details: "“Objective” to rank at a high point."
    },
    {
      title: "P - “Passion”",
      details: "“Passionate” to deliver quality results."
    },
    {
      title: "L - “Loyalty”",
      details: "“Loyalty” to the customer."
    },
    {
      title: "E - “Excellence”",
      details: "“Excellence” in providing the result."
    },
    {
      title: "S - “Success”",
      details: "“Success” Through Collaboration."
    }
  ];
  return (
    <>
      <section
        className="d-flex justify-content-evenly align-items-center about"
        style={{ backgroundColor: "#efefef" }}
      >
        <motion.div
          className="aboutimg1 d-flex flex-column"
          ref={refLeft}
          initial="hidden"
          animate={controlsLeft}
          variants={containerVariants}
        >
          <motion.div variants={itemVariantsLeft}>
            <img
              src="/images/abt1.webp"
              alt=""
              className="abtimg1"
              style={{ marginTop: "-100px", height: "184px", width: "154px", objectFit: "contain" }}
              loading="lazy"
            />
          </motion.div>
          <motion.div variants={itemVariantsLeft}>
            <img
              src="/images/abt3.webp"
              alt=""
              className="abtimg1"
              style={{ marginRight: "-300px", height: "184px", width: "154px", objectFit: "contain" }}
              loading="lazy"
            />
          </motion.div>
        </motion.div>
        <motion.div
          className="aboutus1 d-flex flex-column align-items-center justify-content-center"
          style={{ lineHeight: "1" }}
          ref={refCenter}
          initial="hidden"
          animate={controlsCenter}
          variants={containerVariants}
        >
          <motion.div variants={itemVariantsCenter}>
            <h2 className="title1 m1 fsh1">Grow your business with MPeoples</h2>
          </motion.div>
          <motion.div variants={itemVariantsCenter}>
            <p className="des mb2 fsp1">
              Are you a small, medium businesses that want to enhance your
              growth, we have effective web solutions for you.
            </p>
          </motion.div>
          <motion.div variants={itemVariantsCenter}>
            <motion.button
              className="buttonn"
              onClick={handleClicked}
              variants={buttonVariants}
              whileHover="hover"
              whileTap="tap"
            >
              <span className="text">Let's Talk &rarr;</span>
              <div className="circle"></div>
            </motion.button>
          </motion.div>
          <motion.div variants={itemVariantsCenter}>
            <img src="/images/aboutusimage.webp" alt="" className="abtimg" style={{ height: "388px", objectFit: "contain" }} />
          </motion.div>
        </motion.div>
        <motion.div
          className="aboutimg1"
          ref={refRight}
          initial="hidden"
          animate={controlsRight}
          variants={containerVariants}
        >
          <motion.div variants={itemVariantsRight}>
            <img
              src="/images/abt2.webp"
              alt=""
              className="abtimg1"
              style={{ marginTop: "-100px", height: "184px", width: "154px", objectFit: "contain" }}
              loading="lazy"
            />
          </motion.div>
          <motion.div variants={itemVariantsRight}>
            <img
              src="/images/abt4.webp"
              alt=""
              className="abtimg1"
              style={{ marginLeft: "-300px", height: "184px", width: "154px", objectFit: "contain" }}
              loading="lazy"
            />
          </motion.div>
        </motion.div>
      </section>
      <section className="m expression">
        <h2 className="fsh2 mb1">
          Why <span style={{ color: "#137eb1" }}>MPeoples</span> is the Right
          Choice for You ?
        </h2>
        <div className="sect10class1">
          <div className="sect10subclass1">
            {letters.map((letter, index) => (
              <div
                className="d-flex flex-column align-items-center position-relative"
                key={index}
              >
                <motion.button
                  class="
                      rounded-circle 
                      p-0 
                      d-flex 
                      justify-content-center 
                      align-items-center 
                      fs-1 
                      fw-bolder 
                      border-0 
                      cursor-pointer
                    "
                  onClick={() => handleClick(index)}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.95 }}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.05 }}
                  style={{
                    background:
                      activeButton === index
                        ? "linear-gradient(180deg, #1CBFEC 0%, #002437 100%)"
                        : "#E9ECEF",
                    color: activeButton === index ? "white" : "#555C6D",
                    transition: "0.3s ease",
                    cursor: "pointer",
                    width: "120px",
                    height: "120px"
                  }}
                >
                  {letter}
                </motion.button>
                <p
                  className="fsp"
                  style={{
                    color: activeButton === index ? "#137eb1" : "#555C6D",
                    textAlign: "center"
                  }}
                >
                  {cleanedContents[index]?.text}
                </p>
              </div>
            ))}
          </div>
          {/* Animated Content Box */}
          <AnimatePresence mode="wait">
            {activeButton !== null && (
              <motion.div
                key={activeButton}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ duration: 0.3, ease: "easeInOut" }}
                style={{
                  marginTop: "40px",
                  fontSize: "20px",
                  fontWeight: "bold",
                  textAlign: "center",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  border: "1px solid black",
                  padding: "20px",
                  margin: "50px auto",
                  borderRadius: "10px",
                  background: "linear-gradient(to right, #137eb1, #79c7ffff)",
                  color: "white",
                  width: "80%",
                  position: "relative"
                }}
              >
                <div
                  style={{
                    position: "absolute",
                    top: "-15px",
                    left: "50%",
                    transform: "translateX(-50%)",
                    width: "0",
                    height: "0",
                    borderLeft: "15px solid transparent",
                    borderRight: "15px solid transparent",
                    borderBottom: "15px solid #137eb1"
                  }}
                ></div>
                <div style={{ width: "90%", textAlign: "left" }}>
                  <motion.p
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.3, ease: "easeInOut" }}
                  >
                    {contents[activeButton]?.text}
                  </motion.p>
                </div>
                <motion.img
                  src={contents[activeButton]?.image}
                  alt=""
                  style={{ width: "100px", height: "101px" }}
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3, ease: "easeInOut" }}
                  loading="lazy"
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </section>
      <section className="our-services">
        <h2 className="fsh2 mb1">
          Why <span style={{ color: "#137eb1" }}>MPeoples</span> is the Right
          Choice for You
        </h2>
        <div className="progress-track"></div>
        <div ref={progressRef} className="progressbar"></div>
        <div ref={progressLineRef} className="progress-line"></div>
        {services1.map((service, index) => (
          <div key={index} className="service-item">
            <h3
              className="service-title fsh4"
              onClick={(event) => toggleSection(index, event)}
            >
              <span>{openSection === index ? "▼" : "▶"}</span> {service.title}
            </h3>
            <div
              className={`service-content ${openSection === index ? "open" : ""
                }`}
            >
              <p className="fsp">{service.details}</p>
            </div>
          </div>
        ))}
      </section>
      <motion.section
        className="m d-flex flex-column justify-content-center align-items-center whychooseus"
        ref={ref}
        initial="hidden"
        animate={controls}
        variants={containerVariants}
      >
        <motion.div className="sec2 col-10" variants={containerVariants}>
          <motion.div variants={itemVariants}>
            <h2 className="sec2abt1 fsh2 m0 mb2">
              We're Better. Here's Why...
            </h2>
          </motion.div>
          <motion.div variants={itemVariants}>
            <p className="sec2abt2 fsp m0 mb1">
              We provide strategic design, best custom web development, mobile
              app development and ROI-focused marketing services to maximize
              your brand's potential.
            </p>
          </motion.div>
        </motion.div>

        <motion.div className="mainsec" variants={containerVariants}>
          <motion.div className="abtsec2">
            {services.map((service, index) => (
              <motion.div
                className="abtsec2class d-flex"
                key={index}
                custom={index}
                variants={cardVariants}
              >
                <motion.div>
                  <img
                    src={service.img}
                    alt=""
                    className="imagi"
                    height="100px"
                    width="91px"
                  />
                </motion.div>
                <div className="abtsec2class2">
                  <h3 className="abtsec2class2h4 fsh3">{service.title}</h3>
                  <p className="abtsec2class2p fsp">{service.description}</p>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </motion.div>
      </motion.section>
      <section className="m">
        <div className="row align-items-center justify-content-around">
          <div className="col-12 col-md-5 text-start">
            <motion.div
              initial={{ x: -100, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: "easeOut" }}
            >
              <h4 className="fsh2">WE HELP OUR CLIENTS TO GROW</h4>
              <h3 className="fsh3">Let's start your journey with us</h3>
              <p className="fsp">
                MPeoples Business Solutions help to deliver digital transformation at every stage of the
                product journey, from early ideation to research, prototyping,
                testing, launch, and ongoing support. Explore our end-to-end
                services designed to improve business performance and deliver
                delightful digital experiences.
              </p>
            </motion.div>
          </div>
          <div className="col-12 col-md-5 text-center mt-4 mt-md-0">
            <motion.div
              initial={{ x: 100, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: "easeOut" }}
            >
              <img
                src="/images/abtdis.webp"
                alt="About Us"
                className="img-fluid"
              />
            </motion.div>
          </div>
        </div>
      </section>
      <section className="m whychooseus">
        <div
          className="row d-flex justify-content-evenly tl p rounded"
          style={{ background: "#EFEFEF" }}
        >
          <div className="col-12 col-lg-4 col-md-6 text-start">
            <motion.div
              initial={{ x: -100, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: "easeOut" }}
            >
              <h2 className="fsh2 misviscontent m0 mb2">
                <img src="/images/mis.webp" alt="" className="misvis" loading="lazy" />
                Our Mission
              </h2>
              <p className="fsp">
                Our mission is to empower businesses and individuals by
                delivering innovative, reliable, and scalable software solutions
                that improve progress. We are committed to using leading
                technologies to create custom, user-friendly applications that
                solve real-world problems, foster growth, and enhance the user
                experience. By focusing on collaboration, continuous
                improvement, and a customer-centric approach, we aim to be a
                trusted partner to our clients.
              </p>
            </motion.div>
          </div>
          <div className=" col-lg-4 col-md-4 text-center misvisimg">
            <motion.div
              initial={{ y: 100, opacity: 0 }}
              whileInView={{ y: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: "easeOut" }}
            >
              <img
                src="/images/mv.webp"
                alt="Mission Vision"
                className="misvisimg"
                style={{ maxWidth: "367px", height: "auto" }}
                loading="lazy"
              />
            </motion.div>
          </div>
          <div className="col-12 col-lg-4 col-md-6 text-start">
            <motion.div
              initial={{ x: 100, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: "easeOut" }}
            >
              <h2 className="fsh2 misviscontent m0 mb2">
                <img
                  src="/images/vis.webp"
                  alt=""
                  className="misvis"
                  loading="lazy"
                />
                Our Vision
              </h2>
              <p className="fsp">
                We aim to be the leading IT solutions provider by applying the
                power of technology and innovation. Our goal is to deliver
                advanced, scalable digital solutions that transform businesses,
                boost productivity, and create long-term value. By prioritizing
                quality and innovation, we set new industry benchmarks and shape
                the future of technology-driven solutions worldwide.
              </p>
            </motion.div>
          </div>
        </div>
      </section>
      <section className="sect5 m">
        <div
          className="row d-flex align-items-center rounded justify-content-around"
          style={{
            background:
              "linear-gradient(to left, #137eb1, #79c7ffff)"
          }}
        >
          <div className="col-5 tl">
            <motion.div
              initial={{ x: -100, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: "easeOut" }}
            >
              <h2 className="fsh2">Our Motto</h2>
              <p className="fsp">
                Our goal is to become the leading web solutions provider. By
                partnering with the clients and building reliable, mutually
                beneficial relationships, we aim to achieve growth and profit.
                We achieve to establish a strong, unique global brand identity.
              </p>
            </motion.div>
          </div>
          <div className="col-5">
            <motion.div
              initial={{ x: 100, opacity: 0 }}
              whileInView={{ x: 0, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease: "easeOut" }}
            >
              <img
                src="/images/group1.webp"
                alt=""
                className="ourmottoimg"
                height="366px"
                width="366px"
                loading="lazy"
              />
            </motion.div>
          </div>
        </div>
      </section>
      <section className="m whychooseus">
        <div className="d-flex flex-column justify-content-start align-item-center m-1">
          <h2 className="col-12 m0 mb2 fsh2">Our People Story</h2>
          <div className="col-12 justify-content-md-center justify-content-left d-flex">
            <p className="col-10 fsp">
              MPeoples started with a simple idea—help businesses grow with the
              right solutions. What began as a small team with big dreams has
              grown through hard work, dedication, and a commitment to making a
              real impact. Every step of our journey has been shaped by the
              people who believe in our vision.
            </p>
          </div>
        </div>
      </section>
      {/* NEW PRODUCTS SECTION */}
      <section className="products-section m">
        <motion.div
          className="container"
          initial={{ opacity: 0, y: 50 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
        >
          <div className="text-center mb-5">
            <h2 className="section-title fsh2 mb-3" style={{ color: "#137eb1" }}>
              Our Products & Services
            </h2>
            <p className="section-subtitle fsp col-md-8 mx-auto">
              We deliver cutting-edge digital solutions tailored to your business needs.
              Click on any product to explore detailed features and benefits.
            </p>
          </div>

          <div className="row justify-content-center g-4">
            {products.map((product, index) => (
              <div className="col-md-4 col-sm-6" key={product.id}>
                <motion.div
                  className="product-card"
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  whileHover={{ y: -10, transition: { duration: 0.2 } }}
                  onClick={() => handleProductClick(product)}
                >
                  <div
                    className="product-icon"
                    style={{ backgroundColor: `${product.color}15` }}
                  >
                    <span style={{ fontSize: "2.5rem" }}>{product.icon}</span>
                  </div>
                  <h3 className="product-title fsh3 mt-4">{product.title}</h3>
                  <p className="product-description fsp">{product.description}</p>
                  <div className="product-highlights">
                    {product.details.features.slice(0, 3).map((feature, idx) => (
                      <span key={idx} className="highlight-tag">
                        {feature}
                      </span>
                    ))}
                  </div>
                  <button className="product-cta">
                    Explore <span>→</span>
                  </button>
                </motion.div>
              </div>
            ))}
          </div>

          <div className="text-center mt-5">
            <motion.button
              className="consultation-btn"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={handleClicked}
            >
              Schedule Free Consultation
            </motion.button>
          </div>
        </motion.div>

        {/* Product Modal */}
        {showProductModal && selectedProduct && (
          <ProductModal
            product={selectedProduct}
            onClose={() => setShowProductModal(false)}
          />
        )}
      </section>
      <ContactPage />
    </>
  );
};
export default Aboutus;
